import React, { useEffect, useState } from 'react';
import { auth, isFirebaseConfigured } from '../services/firebase/config';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';

interface FirebaseInitializerProps {
  children: React.ReactNode;
}

const FirebaseInitializer: React.FC<FirebaseInitializerProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFirebaseConfigured()) {
      console.error('Firebase is not properly configured');
      setError('Firebase configuration is missing');
      setIsInitialized(true); // Allow app to load without Firebase
      return;
    }

    const initializeAuth = async () => {
      try {
        // Wait for auth to be ready
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!user) {
            // No user signed in, try anonymous auth
            try {
              console.log('No user found, attempting anonymous sign-in...');
              await signInAnonymously(auth);
            } catch (anonError: any) {
              console.error('Anonymous sign-in failed:', anonError);
              // Continue without auth if anonymous fails
              if (anonError.code === 'auth/operation-not-allowed') {
                setError('Anonymous authentication is not enabled. Please enable it in Firebase Console.');
              }
            }
          } else {
            console.log('User authenticated:', user.isAnonymous ? 'Anonymous' : 'Registered');
          }
          setIsInitialized(true);
        });

        // Cleanup
        return () => unsubscribe();
      } catch (error: any) {
        console.error('Firebase initialization error:', error);
        setError(error.message);
        setIsInitialized(true); // Allow app to load even if Firebase fails
      }
    };

    initializeAuth();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Initializing app...</p>
        </div>
      </div>
    );
  }

  if (error && process.env.NODE_ENV === 'development') {
    console.warn('Firebase initialization warning:', error);
  }

  return <>{children}</>;
};

export default FirebaseInitializer;